import { Fragment, useMemo, useState } from 'react';

import SimpleModalInfo, {
  SimpleModalInfoTitle,
} from 'lib/modal/simple-modal-info';
import {
  AbbrStyled,
  Heading16,
  PStyled,
} from 'approot/shared/styles/typography.style';
import { BadgeSmallStyled } from 'approot/shared/badge/badge.style';
import {
  OutcomeLinkList,
  OutcomePill,
} from 'approot/shared/outcomes/outcomes-shared.style';
import { OUTCOME_MODAL_SIZE } from 'approot/shared/outcomes/outcome.constants';
import {
  ENGLISH_CONNECTION,
  MATH_CONNECTION,
} from 'approot/shared/teaching-learning-sequence/teaching-learning-sequence.constants';
import { ENGLISH } from 'global/constants';
import { InquisitiveSubject } from 'global/graphql/scalars.type';
import type { GetLessonTeachingAndLearningSequenceQuery } from '../teaching-notes.graphql.generated';
import AccordionToggleNew from 'approot/shared/accordion/accordion-toggle-new';
import { grey7 } from 'approot/shared/styles/colours.style';
import { spacing12, spacing4 } from 'approot/shared/styles/spacing.style';
import { ExtraConnectionsList } from './extra-connections.style';
import type { Pluck } from 'lib/gql/pluck';

const ExtraConnectionsView = ({
  connections,
  displayExpanded,
  title,
}: {
  connections: Pluck<
    GetLessonTeachingAndLearningSequenceQuery,
    'getLessonTeachingAndLearningSequence.connections'
  >[];
  displayExpanded: boolean;
  title: string;
}) => {
  const [outcomeId, setOutcomeId] = useState<number | undefined>();

  const selected = useMemo(() => {
    let selected: {
      subject: InquisitiveSubject;
      outcome: Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence.connections.outcomes'
      > | null;
    } = {
      subject: ENGLISH,
      outcome: null,
    };
    connections.forEach(connection => {
      const selectedOutcome = connection.outcomes?.find(
        o => o.id === outcomeId
      );
      if (selectedOutcome) {
        selected.subject = connection.subject as InquisitiveSubject;
        selected.outcome = selectedOutcome;
      }
    });
    return selected;
  }, [connections, outcomeId]);

  if (connections.every(conn => conn.outcomes?.length === 0)) {
    return null;
  }

  return (
    <>
      {selected.outcome && (
        <SimpleModalInfo
          title={
            selected.subject === ENGLISH ? ENGLISH_CONNECTION : MATH_CONNECTION
          }
          titleElement={
            <SimpleModalInfoTitle
              title={
                selected.subject === ENGLISH
                  ? ENGLISH_CONNECTION
                  : MATH_CONNECTION
              }
            />
          }
          isVisible
          size={OUTCOME_MODAL_SIZE}
          dataTracking={{
            label: 'outcome-connections',
          }}
          onClose={() => setOutcomeId(undefined)}
        >
          <Heading16 as="h2" marginBottom={spacing4}>
            {selected.outcome.code}
          </Heading16>
          <PStyled>{selected.outcome.description}</PStyled>
          <OutcomeLinkList>
            <li>
              <BadgeSmallStyled>{selected.outcome.category}</BadgeSmallStyled>
            </li>
            <li>
              <BadgeSmallStyled>{selected.outcome.type}</BadgeSmallStyled>
            </li>
          </OutcomeLinkList>
        </SimpleModalInfo>
      )}

      <AccordionToggleNew
        title={title}
        withDividers
        displayExpanded={displayExpanded}
      >
        {connections.map((connection, index) =>
          connection.outcomes && connection.outcomes.length > 0 ? (
            <Fragment key={index}>
              <PStyled
                marginBottom={spacing4}
                marginTop={spacing12}
                colour={grey7}
              >
                {connection.subject === ENGLISH ? (
                  <>
                    <AbbrStyled
                      aria-label="English Language Arts"
                      title="English Language Arts"
                    >
                      ELA
                    </AbbrStyled>{' '}
                    connections
                  </>
                ) : (
                  MATH_CONNECTION
                )}
              </PStyled>

              <ExtraConnectionsList>
                {connection.outcomes.map(outcome => {
                  return (
                    <li key={outcome.code}>
                      <OutcomePill
                        type="button"
                        aria-label={`Outcome: ${outcome.code}`}
                        aria-haspopup="dialog"
                        onClick={() => setOutcomeId(outcome.id)}
                      >
                        {outcome.code}
                      </OutcomePill>
                    </li>
                  );
                })}
              </ExtraConnectionsList>
            </Fragment>
          ) : null
        )}
      </AccordionToggleNew>
    </>
  );
};

export default ExtraConnectionsView;
